import React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { proposalTokens } from './tokens';

interface Activity {
  id: string;
  title: string;
  category: string;
  image: string;
  description?: string;
  duration?: string;
  intensity?: 'Active' | 'Moderate' | 'Relaxed';
}

interface ActivityDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  activity: Activity | null;
}

export function ActivityDetailsModal({ 
  isOpen, 
  onClose, 
  activity 
}: ActivityDetailsModalProps) {
  if (!isOpen || !activity) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div 
        className={cn(
          "w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] animate-fade-in relative",
          "bg-cream"
        )}
        style={{ backgroundColor: proposalTokens.colors.cream }}
        onClick={e => e.stopPropagation()}
      >
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-10 h-10 rounded-md bg-black/5 hover:bg-black/10 flex items-center justify-center transition-colors"
        >
          <X className="w-5 h-5" style={{ color: proposalTokens.colors.teal }} />
        </button>

        {/* Content Container - Scrollable */}
        <div className="overflow-y-auto max-h-[90vh]">
          {/* Image */}
          <div className="relative h-[250px] md:h-[300px] w-full bg-black overflow-hidden">
            <img 
              src={activity.image} 
              className="w-full h-full object-cover" 
              alt={activity.title}
            />
            {/* Category Badge */}
            <div className="absolute top-4 left-4">
              <span 
                className="text-black text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider" 
                style={{ backgroundColor: proposalTokens.colors.gold }}
              >
                {activity.category}
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 md:p-8">
            {/* Title */}
            <h2 
              className="text-2xl md:text-3xl font-serif font-bold mb-4" 
              style={{ 
                color: proposalTokens.colors.teal, 
                fontFamily: proposalTokens.typography.fontFamily.serif.join(', ') 
              }}
            >
              {activity.title}
            </h2>

            {/* Description */}
            {activity.description && (
              <div className="mb-6">
                <p 
                  className="text-base md:text-lg leading-relaxed whitespace-pre-wrap" 
                  style={{ color: `${proposalTokens.colors.teal}99` }}
                >
                  {activity.description}
                </p>
              </div>
            )}

            {/* Metadata */}
            <div className="flex flex-wrap gap-4 pt-4 border-t border-black/10">
              {activity.duration && (
                <div className="flex items-center text-sm md:text-base" style={{ color: `${proposalTokens.colors.teal}80` }}>
                  <span className="font-medium mr-2">Duration:</span>
                  <span>{activity.duration}</span>
                </div>
              )}
              {activity.intensity && (
                <div className="flex items-center">
                  <span className={cn(
                    "text-xs md:text-sm font-bold uppercase px-3 py-1 rounded",
                    activity.intensity === 'Active' 
                      ? 'bg-red-100 text-red-600' 
                      : activity.intensity === 'Moderate'
                      ? 'text-black'
                      : 'text-teal'
                  )}
                  style={activity.intensity === 'Moderate' 
                    ? { backgroundColor: `${proposalTokens.colors.gold}33` } 
                    : activity.intensity === 'Relaxed' 
                    ? { backgroundColor: `${proposalTokens.colors.teal}1a`, color: proposalTokens.colors.teal } 
                    : undefined}
                  >
                    {activity.intensity}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

